import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/api';
import {starItem,unstarItem} from './favoriteSlice';
import { Folder } from '../../types';
import { folderService } from '../../services/folderService';
import { fileService } from '../../services/fileService';
import { FileItem as LibraryFileItem } from '../../types/files';

import { getDriveApiToken } from '../../lib/auth-token';
export type FileItem = LibraryFileItem;

export interface FolderContent {
  files: FileItem[];
  folders: Folder[];
}

export interface Breadcrumb extends Folder {
  isLast: boolean;
}

interface FolderState {
  folders: Folder[];
  currentFolder: Folder | null;
  /** Last real folder browsed (for global upload target). */
  lastBrowsedFolderId: string | null;
  currentFolderContents: {
    folders: Folder[];
    files: FileItem[];
  } | null;
  breadcrumbs: Breadcrumb[];
  trashFolders: Folder[];
  favorites: (FileItem | Folder)[];
  isLoading: boolean;
  error: string | null;
}

const initialState: FolderState = {
  folders: [],
  currentFolder: null,
  lastBrowsedFolderId: null,
  currentFolderContents: null,
  breadcrumbs: [],
  trashFolders: [],
  favorites: [],
  isLoading: false,
  error: null,
};

// Helper to configure axios with token
const getAxiosConfig = () => {
  const token = getDriveApiToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const fetchFolderContents = createAsyncThunk(
  'folders/fetchFolderContents',
  async (folderId: string | null, { rejectWithValue }) => {
    try {
      // Get folders from the folder service
      const foldersResponse = await folderService.getFolders(folderId, false);
      
      // Get files from the file service
      const filesResponse = await fileService.getFiles(folderId || { type: 'all' });

      return {
        folders: foldersResponse.folders || [],
        files: filesResponse.items || []
      };
    } catch (error: any) {

      return rejectWithValue(error.message || 'Failed to fetch folder contents');
    }
  }
);

export const fetchFolderPath = createAsyncThunk(
  'folders/fetchPath',
  async (folderId: string, { rejectWithValue }) => {
    try {
      const folder = await folderService.getFolder(folderId);
      const path: Breadcrumb[] = [];
      let currentFolder: Folder = folder;
      
      while (currentFolder.parentId) {
        const parent = await folderService.getFolder(currentFolder.parentId);
        path.unshift({ ...parent, isLast: false });
        currentFolder = parent;
      }
      
      if (path.length > 0) {
        path[path.length - 1].isLast = true;
      }
      
      return path;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch folder path');
    }
  }
);

export const createFolder = createAsyncThunk(
  'folders/createFolder',
  async ({ name, parentId }: { name: string; parentId?: string }, { rejectWithValue, dispatch }) => {
    try {

      // First create the folder
      const response = await api.post('/folders/create', { name, parentId }, getAxiosConfig());
      
      // After successful folder creation, refresh the folder contents
      if (response.data && response.data.folder) {

        // Give the backend a moment to finish processing
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Then refresh the contents of the parent folder
        dispatch(fetchFolderContents(parentId || null));
      }
      
      return response.data;
    } catch (error: any) {

      return rejectWithValue(error.response?.data?.message || 'Failed to create folder');
    }
  }
);

export const updateFolder = createAsyncThunk(
  'folders/updateFolder',
  async ({ folderId, name }: { folderId: string; name: string }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/folders/${folderId}`, { name }, getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update folder');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async (folderId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/folders/${folderId}`, getAxiosConfig());
      return folderId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete folder');
    }
  }
);

export const moveItems = createAsyncThunk(
  'folders/moveItems',
  async (
    { items, destinationId }: { items: { id: string; type: 'file' | 'folder' }[]; destinationId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/folders/move', { items, destinationId }, getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to move items');
    }
  }
);

export const fetchFavorites = createAsyncThunk(
  'folders/fetchFavorites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/folders/special/favorites', getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch favorites');
    }
  }
);

export const toggleFavorite = createAsyncThunk(
  'folders/toggleFavorite',
  async ({ id, type }: { id: string; type: 'file' | 'folder' }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/folders/special/favorites/${id}`, { itemType:type }, getAxiosConfig());
      return { id, isStarred:response.data.item.isStarred, type };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle favorite');
    }
  }
);

const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    clearFolderError: (state) => {
      state.error = null;
    },
    setCurrentFolder: (state, action: PayloadAction<Folder | null>) => {
      state.currentFolder = action.payload;
      if (action.payload?.id) {
        state.lastBrowsedFolderId = action.payload.id;
      }
    },
    setLastBrowsedFolderId: (state, action: PayloadAction<string | null>) => {
      state.lastBrowsedFolderId = action.payload;
    },
    clearCurrentFolder: (state) => {
      state.currentFolder = null;
      state.currentFolderContents = null;
      state.breadcrumbs = [];
    },
    /** Clear list UI without wiping lastBrowsedFolderId (upload target for virtual views). */
    clearFolderContents: (state) => {
      state.currentFolder = null;
      state.currentFolderContents = null;
      state.breadcrumbs = [];
    },
    fetchFolderContentsStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchFolderContentsSuccess: (state, action: PayloadAction<{ folders: Folder[]; files: FileItem[] }>) => {
      state.currentFolderContents = action.payload;
      state.isLoading = false;
    },
    fetchFolderContentsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    fetchFolderPathSuccess: (state, action: PayloadAction<Breadcrumb[]>) => {
      state.breadcrumbs = action.payload;
    },
    clearSelection: (state) => {
      if (state.currentFolderContents) {
        state.currentFolderContents = {
          folders: state.currentFolderContents.folders.map((folder: Folder) => ({
            ...folder,
            isSelected: false
          })),
          files: state.currentFolderContents.files.map((file: FileItem) => ({
            ...file,
            isSelected: false
          }))
        };
      }
    },
    toggleFolderSelection: (state, action: PayloadAction<string>) => {
      if (state.currentFolderContents) {
        state.currentFolderContents = {
          ...state.currentFolderContents,
          folders: state.currentFolderContents.folders.map((folder: Folder) => 
            folder.id === action.payload
              ? { ...folder, isSelected: !folder.isSelected }
              : folder
          )
        };
      }
    },
    toggleFileSelection: (state, action: PayloadAction<string>) => {
      if (state.currentFolderContents) {
        state.currentFolderContents = {
          ...state.currentFolderContents,
          files: state.currentFolderContents.files.map((file: FileItem) =>
            file.id === action.payload
              ? { ...file, isSelected: !file.isSelected }
              : file
          )
        };
      }
    },
    selectAll: (state) => {
      if (state.currentFolderContents) {
        state.currentFolderContents = {
          folders: state.currentFolderContents.folders.map((folder: Folder) => ({
            ...folder,
            isSelected: true
          })),
          files: state.currentFolderContents.files.map((file: FileItem) => ({
            ...file,
            isSelected: true
          }))
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Folder Contents
      .addCase(fetchFolderContents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFolderContents.fulfilled, (state, action: PayloadAction<{ folders: Folder[]; files: FileItem[] }>) => {
        state.currentFolderContents = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchFolderContents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Folder Path
      .addCase(fetchFolderPath.fulfilled, (state, action: PayloadAction<Breadcrumb[]>) => {
        state.breadcrumbs = action.payload;
      })
      // Create Folder
      .addCase(createFolder.fulfilled, (state, action: PayloadAction<Folder>) => {
        if (state.currentFolderContents) {
          state.currentFolderContents.folders.push(action.payload);
        }
      })
      // Update Folder
      .addCase(updateFolder.fulfilled, (state, action: PayloadAction<Folder>) => {
        const updatedFolder = action.payload;
        
        if (state.currentFolder && state.currentFolder.id === updatedFolder.id) {
          state.currentFolder = updatedFolder;
        }
        
        if (state.currentFolderContents) {
          const index = state.currentFolderContents.folders.findIndex(
            (folder: Folder) => folder.id === updatedFolder.id
          );
          if (index !== -1) {
            state.currentFolderContents.folders[index] = updatedFolder;
          }
        }
      })
      // Delete Folder
      .addCase(deleteFolder.fulfilled, (state, action: PayloadAction<string>) => {
        const folderId = action.payload;
        
        if (state.currentFolderContents) {
          state.currentFolderContents.folders = state.currentFolderContents.folders.filter(
            (folder: Folder) => folder.id !== folderId
          );
        }
      })
      // Fetch Favorites
      .addCase(fetchFavorites.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFavorites.fulfilled, (state, action: PayloadAction<(FileItem | Folder)[]>) => {
        state.isLoading = false;
        state.favorites = action.payload;
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Toggle Favorite
      .addCase(toggleFavorite.fulfilled, (state, action: PayloadAction<{ id: string; isStarred: boolean; type: 'file' | 'folder' }>) => {
        const { id, isStarred, type } = action.payload;
        
        if (state.currentFolderContents) {
          if (type === 'folder') {
            const folderIndex = state.currentFolderContents.folders.findIndex((folder: Folder) => folder.id === id);
            if (folderIndex !== -1) {
              state.currentFolderContents.folders[folderIndex].isStarred = isStarred;
            }
          } else {
            const fileIndex = state.currentFolderContents.files.findIndex((file: FileItem) => file.id === id);
            if (fileIndex !== -1) {
              state.currentFolderContents.files[fileIndex].isStarred = isStarred;
            }
          }
        }
        
        if (isStarred) {
          const favoriteItem = state.currentFolderContents?.files.find((f: FileItem) => f.id === id) || 
                               state.currentFolderContents?.folders.find((f: Folder) => f.id === id);
          if (favoriteItem && !state.favorites.some((f) => f.id === id)) {
            state.favorites.push(favoriteItem);
          }
        } else {
          state.favorites = state.favorites.filter((item) => item.id !== id);
        }
      })
      .addCase(starItem.fulfilled,(state,action)=>{
        const item=action.payload;
        for(const row of [...(state.currentFolderContents?.files||[]),...(state.currentFolderContents?.folders||[])])if(row.id===item.id)row.isStarred=item.isStarred;
      })
      .addCase(unstarItem.fulfilled,(state,action)=>{
        for(const row of [...(state.currentFolderContents?.files||[]),...(state.currentFolderContents?.folders||[])])if(row.id===action.payload)row.isStarred=false;
        state.favorites=state.favorites.filter(row=>row.id!==action.payload);
      });
  },
});

export const {
  clearFolderError,
  setCurrentFolder,
  setLastBrowsedFolderId,
  clearCurrentFolder,
  clearFolderContents,
  fetchFolderContentsStart,
  fetchFolderContentsSuccess,
  fetchFolderContentsFailure,
  fetchFolderPathSuccess,
  clearSelection,
  toggleFolderSelection,
  toggleFileSelection,
  selectAll
} = folderSlice.actions;

export default folderSlice.reducer; 